import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe, DatePipe, Location } from '@angular/common';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { DeclarationsAuthService } from '../auth/declarations-auth.service';
import { getAsFormGroup } from '../shared/utils';
import {
  ExternalBusinessInterestDeclaration,
  ExternalBusinessInterestDeclarationToAdd
} from './external-business-interest-declaration.model';
import { ExternalBusinessInterestDeclarationService } from './external-business-interest-declaration.service';
import { ExternalBusinessInterestDeclarationAckDialogComponent } from './external-business-interest-declaration-ack-dialog.component';
import { map, Observable } from 'rxjs';
import { AlertDataPreloadedComponent } from '../shared/alert-data-preloaded.component';
import { SwitchModule } from '@syncfusion/ej2-angular-buttons';
import { TextAreaModule, TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { DialogModule } from '@syncfusion/ej2-angular-popups';

@Component({
  selector: 'ad-external-business-interest-declaration',
  templateUrl: './external-business-interest-declaration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AlertDataPreloadedComponent,
    AsyncPipe,
    DatePickerModule,
    DatePipe,
    DropDownListModule,
    ReactiveFormsModule,
    SwitchModule,
    TextAreaModule,
    TextBoxModule,
    DialogModule,
    ExternalBusinessInterestDeclarationAckDialogComponent
  ]
})
export class ExternalBusinessInterestDeclarationComponent implements OnInit {
  protected name$!: Observable<string | null>;
  public form!: UntypedFormGroup;
  public showAckDialog = false;
  public ackDialogData: any = {};

  public other = 'Other';
  public predefinedRoles = ['Controlling Power', 'Director', 'Owner', 'Partner', 'Employee'];
  public roles = this.predefinedRoles.concat([this.other]);
  public declarationDate = new Date();
  public employee!: string;
  public hasLastYearData = false;

  public alreadySubmitted = false;

  public getAsFormGroup = getAsFormGroup;

  constructor(
    public authService: DeclarationsAuthService,
    private fb: UntypedFormBuilder,
    private service: ExternalBusinessInterestDeclarationService,
    private router: Router,
    public location: Location
  ) {}

  ngOnInit(): void {
    this.name$ = this.authService.getActiveAccount$().pipe(map((a) => a?.name || null));

    this.form = this.fb.group({
      additionalInformation: [null],
      businessInterests: this.fb.array([]),
      hasExternalBusinessInterest: [false, Validators.required]
    });

    this.form
      .get('hasExternalBusinessInterest')
      ?.valueChanges.subscribe(() => this.handleHasExternalBusinessInterest());

    this.service.getMine$().subscribe((item) => {
      if (item) {
        this.alreadySubmitted = true;
        this.populateInitialData(item, false);
        this.form.disable();
      } else {
        this.service.getMineFromLastYear$().subscribe((lastYearData) => {
          if (lastYearData) {
            this.populateInitialData(lastYearData, true);
          }
        });
      }
    });
  }

  private populateInitialData(data: ExternalBusinessInterestDeclaration, isLastYearData: boolean): void {
    if (data) {
      this.hasLastYearData = isLastYearData;

      this.form.get('additionalInformation')?.setValue(data.additionalInformation);

      if (data.businessInterests && data.businessInterests.length > 0) {
        this.form.get('hasExternalBusinessInterest')?.setValue(true);

        this.businessInterests.clear();
        for (const businessInterest of data.businessInterests) {
          this.businessInterests.push(
            this.fb.group({
              name: [businessInterest.name, Validators.required],
              role: [
                this.roles.includes(businessInterest.role) ? businessInterest.role : this.other,
                Validators.required
              ],
              roleOther: [
                this.roles.includes(businessInterest.role) ? '' : businessInterest.role,
                RxwebValidators.required({ conditionalExpression: (x: any) => x.role === this.other })
              ],
              dateOfCommencement: [businessInterest.dateOfCommencement, Validators.required]
            })
          );
        }
      }

      if (!isLastYearData) {
        this.declarationDate = data.declarationDate;
      }

      this.employee = data.employee;
    }
  }

  public handleHasExternalBusinessInterest(): void {
    if (this.hasExternalBusinessInterest) {
      if (this.businessInterests.length === 0) {
        this.addBusinessInterest();
      }
    } else {
      this.businessInterests.clear();
    }
  }

  public addBusinessInterest(): void {
    this.businessInterests.push(
      this.fb.group({
        name: ['', Validators.required],
        role: ['', Validators.required],
        roleOther: ['', RxwebValidators.required({ conditionalExpression: (x: any) => x.role === this.other })],
        dateOfCommencement: [null, Validators.required]
      })
    );
  }

  public removeBusinessInterest(index: number): void {
    this.businessInterests.removeAt(index);
  }

  public get businessInterests(): UntypedFormArray {
    return this.form.get('businessInterests') as UntypedFormArray;
  }

  public get hasExternalBusinessInterest(): boolean {
    return !!this.form.get('hasExternalBusinessInterest')?.value;
  }

  public get hasExternalBusinessInterestLabel(): string {
    return this.hasExternalBusinessInterest
      ? 'I confirm that I do have external business interest, the details of which are below:'
      : 'I confirm that I do not have any external business interest.';
  }

  public submitForm(): void {
    this.ackDialogData = {
      name: '',
      declarationDate: this.declarationDate
    };
    this.showAckDialog = true;
  }

  public handleDialogClose(data: any): void {
    if (data && data.name) {
      const value: ExternalBusinessInterestDeclarationToAdd = {
        additionalInformation: this.form.value.additionalInformation,
        businessInterests: this.hasExternalBusinessInterest
          ? this.businessInterests.value.map((interest: any) => ({
              ...interest,
              role: interest.role === this.other ? interest.roleOther : interest.role
            }))
          : null,
        employee: data.name,
        declarationDate: data.declarationDate || this.ackDialogData.declarationDate
      };

      this.form.reset();
      this.form.disable();

      this.service.acknowledge$(value).subscribe(() => this.router.navigate(['/']));
    }
    this.showAckDialog = false;
  }
}
